import {useEffect, useRef, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {useSignUpConfirmationMutation} from '../Auth.api'

export function SignUpConfirmation() {
  const intl = useIntl()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(window.location.search)

  const didSubmit = useRef(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isComplete, setIsComplete] = useState(false)
  const [signupConfirmation] = useSignUpConfirmationMutation()

  const signUpConfirmationSchema = Yup.object().shape({
    organization: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    email: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 50}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    confirmationCode: Yup.string()
      .min(6, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 6}))
      .max(6, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 6}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
  })

  const organization = queryParams.get('organization')
  const email = queryParams.get('email')
  const confirmationCode = queryParams.get('confirmationCode')

  const initialValues = {
    organization: organization ?? '',
    email: email ?? '',
    confirmationCode: confirmationCode ?? '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: signUpConfirmationSchema,
    validateOnMount: true,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      if (!didSubmit.current) {
        try {
          setSubmitting(true)
          const response: any = await signupConfirmation({
            organization: values.organization,
            email: values.email,
            confirmationCode: values.confirmationCode,
          })

          if (response && response.error) {
            const {error} = response
            switch (error.status) {
              case 500:
                setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
                break
              default:
                setError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
            }
          } else {
            setIsComplete(true)
          }
        } catch (e: any) {
          setError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
        } finally {
          setSubmitting(false)
        }
      }
    },
  })

  useEffect(() => {
    const organization = queryParams.get('organization')
    const email = queryParams.get('email')
    const confirmationCode = queryParams.get('confirmationCode')
    if (organization) {
      formik.setFieldValue('organization', organization)
    }
    if (email) {
      formik.setFieldValue('email', email)
    }
    if (confirmationCode) {
      formik.setFieldValue('confirmationCode', confirmationCode)
    }
  }, [])

  return (
    <div className='d-flex flex-column flex-row-auto'>
      {!isComplete && (
        <>
          <div className='d-flex flex-column-fluid flex-start'>
            <h1 className='text-white text-left mb-2'>
              {intl.formatMessage({id: 'AUTH.SIGN_UP_CONFIRMATION.TITLE'})}
            </h1>
          </div>
          <div className='d-flex flex-column-fluid flex-left'>
            <div className='text-white text-left mb-5'>
              {intl.formatMessage({id: 'AUTH.SIGN_UP_CONFIRMATION.DESCRIPTION'})}
            </div>
          </div>
        </>
      )}

      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        // noValidate
      >
        <div className='d-flex flex-column-fluid flex-center'>
          <div className='d-flex flex-column flex-row-fluid'>
            <div className='d-flex flex-column-auto flex-center'>
              {!formik.isSubmitting && error && (
                <div className='mb-5 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{error}</div>
                </div>
              )}
              {!formik.isSubmitting && !error && isComplete && (
                <div className='mb-5 bg-light-info p-8 rounded'>
                  <div className='text-info'>
                    {intl.formatMessage({id: 'AUTH.SIGN_UP_CONFIRMATION.SUCCESS_MESSAGE'})}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {isComplete ? (
          <div className='d-flex flex-row'>
            <div className='d-flex flex-column flex-row-fluid'>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => navigate(`/auth`)}
              >
                <span className='indicator-label'>
                  {intl.formatMessage({id: 'NEXT'}).toUpperCase()}
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className='d-flex flex-column-fluid flex-center'>
              <div className='d-flex flex-column flex-row-fluid'>
                <div className='d-flex flex-column-auto flex-center'>
                  <div className='form fv-row mb-5'>
                    <i className='text-white form-control-icon fa fa-building icon font-weight-bold'></i>
                    <input
                      placeholder={intl.formatMessage({id: 'ORGANIZATION'})}
                      {...formik.getFieldProps('organization')}
                      className={clsx(
                        'placeholder-gray-400 form-control form-control-auth bg-transparent',
                        {
                          'is-invalid': formik.touched.organization && formik.errors.organization,
                        },
                        {
                          'is-valid': formik.touched.organization && !formik.errors.organization,
                        }
                      )}
                      disabled={formik.isSubmitting}
                      type='text'
                      name='organization'
                      autoComplete='off'
                    />
                    {formik.touched.organization && formik.errors.organization && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.organization}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='d-flex flex-column-auto flex-center'>
                  <div className='form fv-row mb-5'>
                    <i className='text-white form-control-icon fa fa-user-circle icon font-weight-bold'></i>
                    <input
                      placeholder={intl.formatMessage({id: 'EMAIL'})}
                      {...formik.getFieldProps('email')}
                      className={clsx(
                        'placeholder-gray-400 form-control form-control-auth bg-transparent',
                        {
                          'is-invalid': formik.touched.email && formik.errors.email,
                        },
                        {
                          'is-valid': formik.touched.email && !formik.errors.email,
                        }
                      )}
                      disabled={formik.isSubmitting}
                      type='text'
                      name='email'
                      autoComplete='off'
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.email}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='d-flex flex-column-auto flex-center'>
                  <div className='form fv-row mb-5'>
                    <i className='text-white form-control-icon fa fa-key icon font-weight-bold'></i>
                    <input
                      placeholder={intl.formatMessage({id: 'CONFIRMATION_CODE'})}
                      {...formik.getFieldProps('confirmationCode')}
                      className={clsx(
                        'placeholder-gray-400 form-control form-control-auth bg-transparent',
                        {
                          'is-invalid':
                            formik.touched.confirmationCode && formik.errors.confirmationCode,
                        },
                        {
                          'is-valid':
                            formik.touched.confirmationCode && !formik.errors.confirmationCode,
                        }
                      )}
                      disabled={formik.isSubmitting}
                      type='text'
                      name='confirmationCode'
                      autoComplete='off'
                    />
                    {formik.touched.confirmationCode && formik.errors.confirmationCode && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.confirmationCode}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex flex-row mt-2'>
              <div className='d-flex flex-column flex-row-fluid'>
                <button
                  type='submit'
                  className='btn btn-sm btn-light'
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {!formik.isSubmitting && (
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'SUBMIT'}).toUpperCase()}
                    </span>
                  )}
                  {formik.isSubmitting && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      {!isComplete && (
        <div className='d-flex flex-row flex-start mt-2'>
          <span className='text-white me-2'>
            {intl.formatMessage({id: 'AUTH.SIGN_IN.BACK_TO'})}
          </span>{' '}
          <Link to='/auth' className='link-primary text-white fw-semibold '>
            <u>{intl.formatMessage({id: 'AUTH.SIGN_IN.TITLE'})}</u>
          </Link>
        </div>
      )}
    </div>
  )
}
